import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Sun, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const MONTH_LABELS_FR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Iter-36/38: year-strip mini calendar of paid leave (CONGE) for the connected user.
 * Renders 12 mini-month grids; each day cell colored by status:
 *   - APPROVED, in quota → sky
 *   - APPROVED, in credit zone → amber
 *   - DRAFT/SUBMITTED → orange (pending)
 *   - blocked / over hard-cap → red ring (shouldn't happen since backend rejects, but defensive)
 * Hidden for freelances. When `alwaysVisible` is true (dedicated /conges page), the strip stays
 * rendered even with zero entries; otherwise it auto-hides when the user has no CONGE this year.
 */
export default function LeaveYearStrip({ alwaysVisible = false }) {
  const [data, setData] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get(`/me/leave-calendar?year=${year}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [year]);

  const byMonth = useMemo(() => {
    const map = {};
    if (!data?.entries) return map;
    for (const e of data.entries) {
      const m = parseInt(e.date.split("-")[1], 10);
      const d = parseInt(e.date.split("-")[2], 10);
      (map[m] = map[m] || {})[d] = e;
    }
    return map;
  }, [data]);

  if (!data || !data.applies) return null;
  if (!alwaysVisible && (!data.entries || data.entries.length === 0)) return null;

  const colorFor = (e) => {
    if (!e) return "bg-zinc-100";
    if (e.blocked) return "bg-red-700 ring-1 ring-red-900";
    if (e.overflow) return "bg-red-500";
    if (e.status === "APPROVED") return "bg-sky-500";
    return "bg-amber-400"; // DRAFT / SUBMITTED / REJECTED / UNLOCK_APPROVED
  };

  const renderMonth = (monthIdx) => {
    const monthNum = monthIdx + 1;
    const last = new Date(year, monthNum, 0).getDate();
    const days = Array.from({ length: last }, (_, i) => i + 1);
    return (
      <div key={monthIdx} className="flex flex-col items-center gap-1">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{MONTH_LABELS_FR[monthIdx]}</div>
        <div className="grid grid-cols-7 gap-[2px]">
          {days.map((d) => {
            const dt = new Date(year, monthIdx, d);
            const dow = dt.getDay();
            const isWeekend = dow === 0 || dow === 6;
            const e = byMonth[monthNum]?.[d];
            const cls = e ? colorFor(e) : (isWeekend ? "bg-zinc-50" : "bg-zinc-100");
            return (
              <span
                key={d}
                title={e ? `${e.date} · ${Math.round(e.day_part * 8)}h · ${e.status}${e.overflow ? " · sur-quota" : ""}` : ""}
                data-testid={e ? `leave-strip-${e.date}` : undefined}
                className={`w-[7px] h-[7px] rounded-[1px] ${cls}`}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const taken = data.entries.reduce((a, e) => a + e.day_part, 0).toFixed(2).replace(/\.?0+$/, "");
  const overflowCount = data.entries.filter((e) => e.overflow && !e.blocked).length;

  return (
    <div data-testid="leave-year-strip" className="bg-white border border-zinc-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <Sun size={18} className="text-sky-600" />
        <h2 className="font-heading text-lg font-bold tracking-tight">My booked leaves in {year}</h2>
        <Link to="/timesheet" className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline">Open timesheet →</Link>
      </div>

      <div className="grid grid-cols-6 lg:grid-cols-12 gap-3 mb-4">
        {Array.from({ length: 12 }).map((_, idx) => renderMonth(idx))}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-600">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-sky-500" /> Approved (quota)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-amber-400" /> Pending / draft</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-red-500" /> Over-quota (credit)</span>
        <span className="ml-auto font-mono text-zinc-500">
          {taken} days cumulated
          {overflowCount > 0 && <span className="ml-2 text-red-700"> · {overflowCount} over-quota <AlertTriangle size={11} className="inline -mt-0.5" /></span>}
        </span>
      </div>
    </div>
  );
}
